import axios, { InternalAxiosRequestConfig } from 'axios';
import { store } from '../stores/store';
import { AppHeaderNames } from '../typegen/app-header-names';
import KioAppHelper from '../helpers/kioApp';
import paths from '../constants/paths';
import { ClientAppHeaderNames } from '../constants/general';
import logger from '../helpers/logger';
import { runInAction } from 'mobx';

// Track active API calls
let activeCallsCount = 0;

// Create axios object
const agent = axios.create({
	baseURL: import.meta.env.VITE_API_URL,
});

let _kioskId: number | null = null;
const getKioskId = () => {
	return _kioskId ? _kioskId : store.kioskStore.kioskId ? store.kioskStore.kioskId : 0;
};

let _apiKey: string | null = null;
const getApiKey = () => {
	return _apiKey
		? _apiKey
		: import.meta.env.DEV && import.meta.env.VITE_IGNORE_KW_AUTH
			? import.meta.env.VITE_BPK_APIKEY
			: KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_APIKEY);
};

const setApiCredentials = (kioskId: number, apiKey: string) => {
	_kioskId = kioskId;
	_apiKey = apiKey;
};

const addApiKeyHeader = <T>(config: InternalAxiosRequestConfig<T>) => {
	const requestUrl = new URL(config.url!, config.baseURL);
	const apiUrl = new URL(import.meta.env.VITE_API_URL);
	// Add required headers to work with the BPK API
	config.headers[AppHeaderNames.kioskId] = getKioskId() ?? '';
	if (requestUrl.origin !== apiUrl.origin || requestUrl.pathname === paths.kiosksAuth) return config;
	config.headers[AppHeaderNames.apiKey] = getApiKey();
	config.headers[AppHeaderNames.kioskSessionId] = store.sessionStore.sessionId;
	return config;
};

// Modify request
agent.interceptors.request.use((config) => {
	// Increment active calls counter
	activeCallsCount++;
	runInAction(() => {
		store.uiStore.activeApiCalls = activeCallsCount;
	});

	// Update busy state
	store.uiStore.isBusy();

	// Disable certain call types
	try {
		const callType = config.headers?.[ClientAppHeaderNames.apiCallType];
		const disabledApiCalls = import.meta.env.VITE_DISABLE_API_CALL_TYPE;
		if (!callType || !disabledApiCalls) return addApiKeyHeader(config);
		const callTypeNumber = parseInt(callType, 10);
		const disabledApiCallsNumber = parseInt(disabledApiCalls, 10);
		if (disabledApiCallsNumber === 0 || (disabledApiCallsNumber & callTypeNumber) !== 0) {
			logger.debug.warn(`The API call type ${callType} has been disabled on this environment.`);
			return Promise.reject(new axios.Cancel('This API call type has been disabled on this environment'));
		}
	} catch (error) {
		logger.debug.error('There was a problem processing the API call type', error);
	} finally {
		if (config.headers?.[ClientAppHeaderNames.apiCallType]) delete config.headers[ClientAppHeaderNames.apiCallType];
	}

	return addApiKeyHeader(config);
});

// Delay function
const sleep = (delay: number) => {
	return new Promise((resolve) => {
		setTimeout(resolve, delay);
	});
};

// Modify response
agent.interceptors.response.use(
	async (response) => {
		if (import.meta.env.DEV) await sleep(1000); // Fake delay

		// Decrement active calls counter
		activeCallsCount = Math.max(0, activeCallsCount - 1);
		runInAction(() => {
			store.uiStore.activeApiCalls = activeCallsCount;
		});

		// Only set idle if no more active calls
		if (activeCallsCount === 0) {
			store.uiStore.isIdle();
		}

		return response;
	},
	async (error) => {
		if (import.meta.env.DEV) await sleep(1000); // Fake delay

		// Decrement active calls counter
		activeCallsCount = Math.max(0, activeCallsCount - 1);
		runInAction(() => {
			store.uiStore.activeApiCalls = activeCallsCount;
		});

		// Only set idle if no more active calls
		if (activeCallsCount === 0) {
			store.uiStore.isIdle();
		}

		return Promise.reject(error);
	}
);

export default agent;
export { getKioskId, getApiKey, setApiCredentials };
